import * as GC from '@mescius/spread-sheets';

import {
  collectUniqueLoops,
  findLoopsAffectedByCellShift,
  findLoopsIntersectingColumns,
  removeLoopById,
} from './loop';
import { translate } from '../utils/translations';

type SafeAddColumnsCommandOptions = {
  sheetName: string;
  col: number;
  colCount?: number;
  loopIds: number[];
};

type SafeDeleteColumnsCommandOptions = {
  sheetName: string;
  col: number;
  colCount?: number;
  loopIds: number[];
};

type SafeCellShiftCommandOptions = {
  sheetName: string;
  range: GC.Spread.Sheets.Range;
  loopIds: number[];
};

type ConfirmFn = (params: { message: string }) => Promise<boolean>;

/**
 * 列の追加・削除を行うコマンド
 */
function registerSafeColumnCommand(
  spread: GC.Spread.Sheets.Workbook,
  commandName: 'safeAddColumns' | 'safeDeleteColumns',
  applyOperation: (
    sheet: GC.Spread.Sheets.Worksheet,
    options: SafeAddColumnsCommandOptions | SafeDeleteColumnsCommandOptions
  ) => void
): void {
  const commandManager = spread.commandManager();

  commandManager.register(commandName, {
    canUndo: true,
    execute: (
      context: GC.Spread.Sheets.Workbook,
      options: SafeAddColumnsCommandOptions | SafeDeleteColumnsCommandOptions,
      isUndo: boolean
    ): boolean => {
      const Commands = GC.Spread.Sheets.Commands;

      if (isUndo) {
        Commands.undoTransaction(context, options);
        return true;
      }

      const sheet = context.getSheetFromName(options.sheetName);
      if (!sheet) {
        return false;
      }

      Commands.startTransaction(context, options);

      try {
        for (const loopId of options.loopIds) {
          removeLoopById(sheet, loopId);
        }

        applyOperation(sheet, options);

        Commands.endTransaction(context, options);
        return true;
      } catch (error) {
        Commands.undoTransaction(context, options);
        throw error;
      }
    },
  });
}

/**
 *  セルの左右方向への追加・削除を行うカスタムコマンド
 */
function registerSafeCellShiftCommand(
  spread: GC.Spread.Sheets.Workbook,
  commandName: 'safeInsertCellsRight' | 'safeDeleteCellsLeft',
  baseCommandName: 'Designer.insertRightCells' | 'Designer.deleteLeftCells'
): void {
  const commandManager = spread.commandManager();

  commandManager.register(commandName, {
    canUndo: true,
    execute: (
      context: GC.Spread.Sheets.Workbook,
      options: SafeCellShiftCommandOptions,
      isUndo: boolean
    ): boolean => {
      const Commands = GC.Spread.Sheets.Commands;

      if (isUndo) {
        Commands.undoTransaction(context, options);
        return true;
      }

      const sheet = context.getSheetFromName(options.sheetName);
      if (!sheet) {
        return false;
      }

      Commands.startTransaction(context, options);

      try {
        for (const loopId of options.loopIds) {
          removeLoopById(sheet, loopId);
        }

        context.commandManager().execute({
          cmd: baseCommandName,
          sheetName: options.sheetName,
          selections: [options.range],
        });

        Commands.endTransaction(context, options);
        return true;
      } catch (error) {
        Commands.undoTransaction(context, options);
        throw error;
      }
    },
  });
}

function registerSafeAddColumnsCommand(
  spread: GC.Spread.Sheets.Workbook
): void {
  registerSafeColumnCommand(
    spread,
    'safeAddColumns',
    (
      sheet: GC.Spread.Sheets.Worksheet,
      options: SafeAddColumnsCommandOptions | SafeDeleteColumnsCommandOptions
    ): void => {
      sheet.addColumns(options.col, options.colCount ?? 1);
    }
  );
}

function registerSafeDeleteColumnsCommand(
  spread: GC.Spread.Sheets.Workbook
): void {
  registerSafeColumnCommand(
    spread,
    'safeDeleteColumns',
    (
      sheet: GC.Spread.Sheets.Worksheet,
      options: SafeAddColumnsCommandOptions | SafeDeleteColumnsCommandOptions
    ): void => {
      sheet.deleteColumns(options.col, options.colCount ?? 1);
    }
  );
}

function registerSafeInsertCellsRightCommand(
  spread: GC.Spread.Sheets.Workbook
): void {
  registerSafeCellShiftCommand(
    spread,
    'safeInsertCellsRight',
    'Designer.insertRightCells'
  );
}

function registerSafeDeleteCellsLeftCommand(
  spread: GC.Spread.Sheets.Workbook
): void {
  registerSafeCellShiftCommand(
    spread,
    'safeDeleteCellsLeft',
    'Designer.deleteLeftCells'
  );
}

function registerSafeColumnCommands(spread: GC.Spread.Sheets.Workbook): void {
  registerSafeAddColumnsCommand(spread);
  registerSafeDeleteColumnsCommand(spread);
  registerSafeInsertCellsRightCommand(spread);
  registerSafeDeleteCellsLeftCommand(spread);
}

function isFullColumnSelection(
  sheet: GC.Spread.Sheets.Worksheet,
  range: GC.Spread.Sheets.Range
): boolean {
  return range.row === -1 || range.rowCount >= sheet.getRowCount();
}

export function registerColumnChangeHandler(
  spread: GC.Spread.Sheets.Workbook,
  confirm: ConfirmFn
): void {
  // 関連するカスタムコマンドの登録
  registerSafeColumnCommands(spread);

  // 列の挿入・削除、セルの左右方向への挿入・削除のハンドリング
  spread.bind(
    GC.Spread.Sheets.Events.ColumnChanging,
    async (
      _e: unknown,
      args: GC.Spread.Sheets.IColumnChangedEventArgs & { cancel: boolean }
    ): Promise<void> => {
      const sheet = args.sheet;
      if (!sheet) {
        return;
      }

      const selection = sheet.getSelections()[0];
      if (!selection) {
        return;
      }

      const count = args.count ?? 1;

      const isInsert = args.propertyName === 'addColumns';
      const isDelete = args.propertyName === 'deleteColumns';

      if (!isInsert && !isDelete) {
        return;
      }

      // 列全体の挿入・削除
      if (isFullColumnSelection(sheet, selection)) {
        const loops = collectUniqueLoops(
          findLoopsIntersectingColumns(sheet, args.col, count)
        );

        if (loops.length === 0) {
          return;
        }

        args.cancel = true;

        const ok = await confirm({
          message: isInsert
            ? translate('ADD_COLUMN_OPERATION_WILL_CHANGE_LOOP_RANGE_STRUCTURE')
            : translate(
                'DELETE_COLUMN_OPERATION_WILL_CHANGE_LOOP_RANGE_STRUCTURE'
              ),
        });
        spread.focus(true);

        if (!ok) {
          return;
        }

        spread.commandManager().execute({
          cmd: isInsert ? 'safeAddColumns' : 'safeDeleteColumns',
          sheetName: sheet.name(),
          col: args.col,
          colCount: count,
          loopIds: loops.map((loop) => loop.loopId),
        });

        return;
      }

      // セルの右方向への挿入・削除
      const loops = collectUniqueLoops(
        findLoopsAffectedByCellShift(
          sheet,
          selection,
          isInsert ? 'insertRight' : 'deleteLeft'
        )
      );

      if (loops.length === 0) {
        return;
      }

      args.cancel = true;

      const ok = await confirm({
        message: isInsert
          ? translate('ADD_CELL_OPERATION_WILL_CHANGE_LOOP_RANGE_STRUCTURE')
          : translate('DELETE_CELL_OPERATION_WILL_CHANGE_LOOP_RANGE_STRUCTURE'),
      });
      spread.focus(true);

      if (!ok) {
        return;
      }

      spread.commandManager().execute({
        cmd: isInsert ? 'safeInsertCellsRight' : 'safeDeleteCellsLeft',
        sheetName: sheet.name(),
        range: selection,
        loopIds: loops.map((loop) => loop.loopId),
      });
    }
  );

  const originalDeleteLeftCells =
    // @ts-ignore
    spread.commandManager()['Designer.deleteLeftCells'];

  // NOTE: セル削除はColumnChangingで拾えないため、直接コマンドをカスタムする
  spread.commandManager().register('Designer.deleteLeftCells', {
    canUndo: true,
    execute: async (
      context: GC.Spread.Sheets.Workbook,
      options: { selections: GC.Spread.Sheets.Range[]; sheetName: string },
      isUndo: boolean
    ): Promise<void> => {
      const Commands = GC.Spread.Sheets.Commands;

      if (isUndo) {
        Commands.undoTransaction(context, options);
        return;
      }

      const sheet = context.getSheetFromName(options.sheetName);
      if (!sheet) {
        return;
      }
      const selection = options.selections[0];

      // セルの左方向への削除
      const loops = collectUniqueLoops(
        findLoopsAffectedByCellShift(sheet, selection, 'deleteLeft')
      );

      if (loops.length > 0) {
        const ok = await confirm({
          message: translate(
            'DELETE_CELL_OPERATION_WILL_CHANGE_LOOP_RANGE_STRUCTURE'
          ),
        });

        spread.focus();

        if (!ok) return;
      }

      Commands.startTransaction(context, options);

      try {
        for (const loopId of loops.map((loop) => loop.loopId)) {
          removeLoopById(sheet, loopId);
        }

        originalDeleteLeftCells.execute(context, options, false);

        Commands.endTransaction(context, options);
        return;
      } catch (error) {
        Commands.undoTransaction(context, options);
        throw error;
      }
    },
  });
}
